import { AfterViewInit, Directive, ElementRef, Injector, Input, OnDestroy, Renderer2, TemplateRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';
import { TooltipConfig, TooltipHorizontalPosition, TooltipPosition, TooltipSize } from './tooltip-config';

@Directive({
  selector: '[mtgTooltip]'
})
export class TooltipDirective implements AfterViewInit, OnDestroy {
  private overlayRef: OverlayRef;
  private subscriptions: (() => void)[] = [];

  @Input('mtgTooltip')
  content: string|TemplateRef<any>;

  @Input('mtgTooltipTrigger')
  trigger: 'click' | 'hover' | 'manual' = 'hover';

  @Input('mtgTooltipPosition')
  position: TooltipPosition = 'top';

  // tslint:disable-next-line:no-input-rename
  @Input('mtgTooltipHorizontalPosition')
  horizontalPosition: TooltipHorizontalPosition = 'center';

  @Input('mtgTooltipSize')
  size: TooltipSize;

  @Input('mtgTooltipVisible')
  set isVisible(visible: boolean) {
    if (visible) {
      this.showTooltip();
    } else {
      this.hideTooltip();
    }
  }

  @Input('mtgTooltipDisabled')
  disabled: boolean;

  constructor(
    private overlay: Overlay,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private injector: Injector,
  ) {}

  ngAfterViewInit(): void {
    if (this.trigger === 'click') {
      this.subscriptions.push(
        this.renderer.listen(this.elementRef.nativeElement, 'click', (event) => {
          if (this.overlayRef) {
            this.hideTooltip();
          } else {
            this.showTooltip();
          }
        })
      );
    }
    if (this.trigger === 'hover') {
      this.subscriptions.push(
        this.renderer.listen(this.elementRef.nativeElement, 'mouseover', (event) => {
          this.showTooltip();
        })
      );
      this.subscriptions.push(
        this.renderer.listen(this.elementRef.nativeElement, 'mouseout', (event) => {
          this.hideTooltip();
        })
      );
    }
  }

  ngOnDestroy(): void {
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
    this.hideTooltip();
  }

  showTooltip() {
    if (this.overlayRef || this.disabled) {
      return;
    }

    this.overlayRef = this.overlay.create({
      positionStrategy: this.getPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'cdk-overlay-tooltip'
    });
    const weekmap = new WeakMap();
    const config = new TooltipConfig();
    config.content = this.content;
    config.position = this.position;
    config.horizontalPosition = this.horizontalPosition;
    config.size = this.size;
    weekmap.set(TooltipConfig, config);
    const injector = new PortalInjector(this.injector, weekmap);
    const componentPortal = new ComponentPortal(TooltipComponent, null, injector);
    this.overlayRef.attach(componentPortal);
  }

  hideTooltip() {
    if (!this.overlayRef) {
      return;
    }
    this.overlayRef.dispose();
    this.overlayRef = null;
  }

  getPositionStrategy() {
    const positions = [];
    if (this.position === 'top') {
      positions.push({
        originX: this.horizontalPosition,
        originY: 'top',
        overlayX: this.horizontalPosition,
        overlayY: 'bottom'
      });
      positions.push({
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      });
      positions.push({
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom'
      });
    }
    if (this.position === 'bottom') {
      positions.push({
        originX: this.horizontalPosition,
        originY: 'bottom',
        overlayX: this.horizontalPosition,
        overlayY: 'top'
      });
    }
    return this.overlay.position()
      .flexibleConnectedTo(this.elementRef.nativeElement)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions(positions);
  }
}
